import Chart from 'chart.js';
import 'chartjs-plugin-datalabels';
import moment from 'moment';
import axios from 'axios';
import {globalActions} from '../store/global-actions';

const chartBackgroundColor = 'rgba(211, 229, 242, 0.7)';
const textColor = 'rgb(33, 21, 105)';
const textAccentColor = 'rgb(247, 57, 191)';
const axisColor = 'rgba(33, 21, 105, 0.3)';
const lineColor = 'rgba(33, 21, 105, 0.7)';
const ticksColor = 'rgba(33, 21, 105, 0.7)';
const lineWidth = 1;
const pointHoverRadius = 3;
const pointRadius = 5;
const maxXTicks = 8;
const maxYTicks = 11;
const chartFontStyle = 'normal';
const ticksFontSize = 14;
const labelPrimarySize = 20;
const labelSecondarySize = 16;
const titleSize = 20;
const titlePadding = 30;
const percentVertDataOffset = 0.1;
const percentHorizDataOffset = 0.1;
const percentHorizDataOffsetMobile = 0.25;
const firstNotFakePointIndex = 1;
const radix = 10;

const priceHistoryChart = {
    template: '#price-history-chart',
    props: {
        chartSelector: {
            type: String,
            required: true,
        },
        chartDataUrl: {
            type: String,
            required: true,
        },
        labels: {
            required: true,
        },
        fontFamily: {
            type: String,
        },
        errorMessage: {
            type: String,
            required: false,
        },
        errorTitle: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            dateFormat: 'YYYY-MM-DD HH:mm:ss',
            chartData: [],
            maxYValue: null,
            minXValue: null,
            maxXValue: null,
            lastNotFakePointIndex: null,
            showChart: false,
        };
    },
    computed: {
        lastChange() {
            const count = this.chartData.length;
            let res = 0;
            if (this.chartData && count > 1) {
                res = this.chartData[this.lastNotFakePointIndex].y - this.chartData[this.lastNotFakePointIndex - 1].y;
            }

            return res;
        },
    },
    mounted() {
        this._initChart();
    },
    methods: {
        _isMobile() {
            const mobileDeviceTriggerWidth = 576;
            return window.innerWidth < mobileDeviceTriggerWidth;
        },
        _prepareChartData(sourceData) {
            const first = sourceData[0];
            const last = sourceData[sourceData.length - 1];

            const startDate = moment(first.when, this.dateFormat);
            const endDate = moment(last.when, this.dateFormat);
            let duration = moment.duration(endDate.diff(startDate));
            let offsetUnit;

            if (duration.asDays() > 1) {
                offsetUnit = 'day';
                duration = moment.duration(endDate.startOf('day').diff(startDate.startOf('day'))).asDays();
            } else {
                offsetUnit = 'minute';
                duration = duration.asMinutes();
            }

            let xOffset = duration *
                (this._isMobile() ?
                    percentHorizDataOffset :
                    percentHorizDataOffsetMobile);
            xOffset = Math.round(Math.max(xOffset, 1));

            const fakeFirstPoint = {
                x: startDate
                    .subtract(xOffset, offsetUnit)
                    .format(this.dateFormat),
                y: first.price,
            };
            const fakeLastPoint = {
                x: endDate.add(xOffset, offsetUnit).format(this.dateFormat),
                y: last.price,
            };

            return [fakeFirstPoint].concat(
                sourceData.map((p) => ({x: p.when, y: p.price})),
                [fakeLastPoint],
            );
        },


        _initChartData(data) {
            const preparedData = this._prepareChartData(data);
            this.chartData = preparedData;
            this.maxYValue = Math.max(...data.map((o) => o.price), 0);
            this.minXValue = this._convertUtcToLocal(preparedData[0].x);
            this.maxXValue = this._convertUtcToLocal(preparedData[preparedData.length - 1].x);
            this.lastNotFakePointIndex = preparedData.length - 2;
        },


        _initChart() {
            axios.get(this.chartDataUrl).then((res) => {
                this.showChart = true;
                this._initChartData(res.data);

                const chartElement = this.$refs.chart;
                const ctx = chartElement.getContext('2d');
                const dataset = Object.assign(
                    {data: this.chartData},
                    this._getDatasetConfig(this.chartData),
                );
                const chart = new Chart(ctx, this._getChartConfig(dataset));
                this._setXAxesBoundaries(chart);
                chart.update();
            })
                .catch(() => {
                    this._handleError();
                });
        },

        _handleError() {
            this.showChart = false;
            globalActions.openInfoModal(this.errorTitle, this.errorMessage);
        },

        _getDatasetLabelConfig() {
            const labelGap = 7;
            const getLabelConfig = (isPrimary) => {
                return {
                    color: isPrimary ? textColor : textAccentColor,
                    font: {
                        size: isPrimary ? labelPrimarySize : labelSecondarySize,
                    },
                    offset: (context) => {
                        return isPrimary && context.dataIndex !== firstNotFakePointIndex ?
                            labelSecondarySize + labelGap :
                            labelGap;
                    },
                    formatter: isPrimary ?
                        this._formatPrimaryLabel :
                        this._formatSecondaryLabel,
                };
            };

            return {
                anchor: 'start',
                align: 'top',
                clamp: true,
                labels: {
                    title: getLabelConfig(true),
                    value: getLabelConfig(false),
                },
            };
        },

        _getDatasetConfig(dataArray) {
            return {
                borderColor: lineColor,
                borderWidth: lineWidth,
                pointBackgroundColor: textColor,
                backgroundColor: chartBackgroundColor,
                fontFamily: this.fontFamily,
                cubicInetrpolation: 'monotone',
                pointRadius: dataArray.map((i, index) =>
                    !this._isBorderPoint(index) ? 0 : pointRadius,
                ),
                pointHoverRadius: dataArray.map((i, index) => {
                    let radius = pointHoverRadius;
                    if (this._isBorderPoint(index)) {
                        radius = pointRadius;
                    }
                    if (index === 0 || index === this.chartData.length - 1) {
                        radius = 0;
                    }
                    return radius;
                }),
                // chartjs-plugin-datalabels configuration
                datalabels: this._getDatasetLabelConfig(),
            };
        },

        _getChartConfig(dataset) {
            return {
                type: 'line',
                data: {
                    datasets: [dataset],
                },
                options: {
                    responsive: true,
                    defaultFontStyle: chartFontStyle,
                    maintainAspectRatio: false,
                    title: this._getChartTitleConfig(),
                    legend: {display: false},
                    tooltips: {
                        enabled: false,
                        intersect: false,
                        mode: 'index',
                        custom: this._getTooltipsConfig,
                    },
                    scales: {
                        xAxes: this._getXAxesConfig(),
                        yAxes: this._getYAxesConfig(),
                    },
                    hover: {
                        axis: 'x',
                        intersect: false,
                    },
                },
            };
        },

        _getTooltipsConfig(tooltipModel) {
            let tooltipEl = document.getElementById('chartjs-tooltip');

            if (!tooltipEl) {
                tooltipEl = document.createElement('div');
                tooltipEl.id = 'chartjs-tooltip';
                tooltipEl.classList.add('price-history-chart__tooltip', 'below');
                this.$el.appendChild(tooltipEl);
            }

            const hiddenTooltipClass = 'price-history-chart__tooltip_hidden';
            if (tooltipModel.opacity === 0 ||
                tooltipModel.dataPoints[0].index === 0 ||
                tooltipModel.dataPoints[0].index === this.chartData.length - 1) {
                if (!tooltipEl.classList.contains(hiddenTooltipClass)) {
                    tooltipEl.classList.add(hiddenTooltipClass);
                }
            } else {
                if (tooltipEl.classList.contains(hiddenTooltipClass)) {
                    tooltipEl.classList.remove(hiddenTooltipClass);
                }

                this._setTooltipText(tooltipModel, tooltipEl);
                this._setTooltipPosition(tooltipModel, tooltipEl);
            }
        },

        _setTooltipText(tooltipModel, tooltipEl) {
            tooltipEl.innerHTML = `<div class="price-history-chart__tooltip-date">
                ${this.$filters.fullDateWithTextMonth(tooltipModel.title[0])}</div>
                <div>${this._formatPrice(tooltipModel.body[0].lines[0])}</div>`;
        },

        _setTooltipPosition(tooltipModel, tooltipEl) {
            const tooltipOffsetMultiplier = 0.5;
            const canvasPadding = parseInt(window.getComputedStyle(this.$el).paddingLeft, radix);
            const tooltipLeft = tooltipModel.caretX + canvasPadding - tooltipEl.offsetWidth * tooltipOffsetMultiplier;
            tooltipEl.style.left = `${tooltipLeft}px`;
            tooltipEl.style.top = `${tooltipModel.caretY}px`;

            const canvasPosition = this.$el.getBoundingClientRect();
            const tooltipElPosition = tooltipEl.getBoundingClientRect();
            if (tooltipElPosition.right > canvasPosition.right) {
                tooltipEl.style.left = `${tooltipLeft + canvasPosition.right - tooltipElPosition.right}px`;
            }
        },

        _getXAxesConfig() {
            const timeFormat = 'HH:mm*D MMM';
            return [
                {
                    type: 'time',
                    time: {
                        parser: (date) => {
                            return moment.utc(date, this.dateFormat).local();
                        },
                        displayFormats: {
                            day: 'D MMM',
                            month: 'MMM',
                            year: 'YYYY',
                            hour: timeFormat,
                            minute: timeFormat,
                            second: timeFormat,
                        },
                    },
                    scaleLabel: {
                        display: false,
                    },
                    ticks: {
                        suggestedMin: this.minXValue,
                        suggestedMax: this.maxXValue,
                        autoSkip: true,
                        maxTicksLimit: maxXTicks,
                        fontColor: ticksColor,
                        fontSize: ticksFontSize,
                        callback: this._formatTimeTicks,
                    },
                    gridLines: this._getGridlinesConfig(),
                },
            ];
        },

        _getYAxesConfig() {
            return [
                {
                    position: 'left',
                    scaleLabel: {
                        display: false,
                    },
                    ticks: {
                        min: 0,
                        suggestedMax:
                            this.maxYValue * (1 + percentVertDataOffset),
                        maxTicksLimit: maxYTicks,
                        fontColor: ticksColor,
                        fontSize: ticksFontSize,
                        callback: this._formatPrice,
                        padding: 5,
                    },
                    gridLines: this._getGridlinesConfig(),
                },
            ];
        },

        _getGridlinesConfig() {
            return {
                drawOnChartArea: false,
                zeroLineWidth: 0,
                lineColor: axisColor,
                lineWidth: 1,
            };
        },

        _getChartTitleConfig() {
            return {
                display: true,
                text: this.labels.chartTitle,
                fontColor: textColor,
                fontSize: titleSize,
                fontStyle: chartFontStyle,
                padding: titlePadding,
            };
        },

        _setXAxesBoundaries(chart) {
            const boundaries = this._getXAxesBoundaries(chart);
            chart.options.scales.xAxes[0].ticks.min = boundaries.min;
            chart.options.scales.xAxes[0].ticks.max = boundaries.max;
        },

        _getXAxesBoundaries() {
            return {
                min: new Date(this.minXValue),
                max: new Date(this.maxXValue),
            };
        },

        _convertUtcToLocal(formattedDate) {
            return moment.utc(formattedDate, this.dateFormat).local().format(this.dateFormat);
        },

        _getChartTimeUnit() {
            const boundaries = this._getXAxesBoundaries();
            let unit;

            if (boundaries.min.getMonth() === boundaries.max.getMonth() &&
                boundaries.min.getDay() === boundaries.max.getDay()) {
                unit = 'day';
            } else if (boundaries.max.getYear() - boundaries.min.getYear() < 1) {
                unit = 'month';
            } else {
                unit = 'year';
            }

            return unit;
        },

        _roundBeforeFormatting(value) {
            const notationScale = 10;
            return Math.round(value * notationScale) / notationScale;
        },

        _formatTimeTicks(value) {
            return value.split('*');
        },

        _formatPrice(value, index, values) {
            const isAxisTicks = values != null;
            const halfDivider = 2;
            if (value == null || (isAxisTicks && index % Math.floor(values.length / halfDivider) !== 0)
            ) {
                return '';
            }

            const thousand = 1000;
            const million = 1000000;
            const thousandLabel = this.labels.thousand;
            const millionLabel = this.labels.million;

            let formattedValue = this._roundBeforeFormatting(value);

            if (Math.abs(value) >= thousand) {
                let labelSuffix = ' ';
                if (Math.abs(value) >= million) {
                    formattedValue = this._roundBeforeFormatting((value / million));
                    labelSuffix += millionLabel;
                } else {
                    formattedValue = this._roundBeforeFormatting((value / thousand));
                    labelSuffix += thousandLabel;
                }

                formattedValue =
                    this._isMobile() && isAxisTicks ?
                        [formattedValue, labelSuffix] :
                        `${formattedValue}${labelSuffix}`;
            }

            return formattedValue;
        },

        _formatPrimaryLabel(value, context) {
            return this._isBorderPoint(context.dataIndex) ?
                `${this._formatPrice(value.y)}` :
                '';
        },

        _formatSecondaryLabel(value, context) {
            const sign = this.lastChange > 0 ? '+' : '';
            return this.lastChange &&
                context.dataIndex === this.lastNotFakePointIndex ?
                `${sign}${this._formatPrice(
                    this.lastChange,
                )}` :
                '';
        },

        _isBorderPoint(index) {
            return index === this.lastNotFakePointIndex || index === firstNotFakePointIndex;
        },
    },
};

function initPriceHistoryChartComponent(app) {
    app.component('price-history-chart', priceHistoryChart);
}

export default initPriceHistoryChartComponent;
